import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Bot, Trash2, ArrowUpRight, Database } from "lucide-react";
import { searchProjectDocs } from "../api/client";
import "./ChatBot.css";

const PRESET_TOPICS = [
  { id: "connect_mt5", label: "🔌 Connect MT5 EA", keyword: "mt5" },
  { id: "check_executions", label: "📊 Check MT5 Executions", keyword: "execute" },
  { id: "license_id", label: "🔑 License ID & Limits", keyword: "license" },
  { id: "webhook_format", label: "📈 Webhook Signals", keyword: "webhook" },
  { id: "plans_pricing", label: "💳 Pricing & Plans", keyword: "plan" },
  { id: "support_channels", label: "💬 Contact Support", keyword: "support" }
];

const PAGE_GUIDES = {
  dashboard: {
    title: "📊 Dashboard Page",
    path: "/dashboard",
    purpose: "Provides a high-level statistical overview of your trading activity, connected licenses, active account bridges, and profit/loss performance graphs.",
    what_to_see: [
      "Key indicators (KPIs) showing active licenses and connected MT5 accounts",
      "A daily profit/loss visual chart tracking trade copier values",
      "Timeframe selection filters in the header",
      "Your current active subscription plan tier and service expiry details"
    ],
    why_use_it: "To get a quick overview of your entire copy bridge operation and check active limits at a glance.",
    keywords: ["dashboard", "home", "main", "overview", "stats", "kpi", "chart", "graph", "profit", "loss", "home screen"]
  },
  signals: {
    title: "🚨 Webhook Signals Page",
    path: "/signals",
    purpose: "Logs all TradingView webhook alert signals received by the copier server in real-time.",
    what_to_see: [
      "A sequential list of received webhook alert signals",
      "The raw JSON payloads sent by TradingView (containing actions, magic numbers, symbols, volumes, and license keys)",
      "Exact arrival timestamps for audit logs and payload details"
    ],
    why_use_it: "To confirm that your TradingView alerts are successfully reaching the copier server and to check signal delivery formatting.",
    keywords: ["signal", "signals", "alert", "alerts", "webhook", "tradingview", "payload", "json payload", "raw json"]
  },
  trades: {
    title: "📈 Executed Trades Page",
    path: "/trades",
    purpose: "Displays the unified history of successfully executed copied trades on your MT5 terminal.",
    what_to_see: [
      "A log of executed copier trades with symbol names and action types (Buy/Sell)",
      "Opening/closing price details and volume amounts",
      "Individual strategy magic numbers and realized profits/losses"
    ],
    why_use_it: "To check whether signals received from TradingView were successfully executed in MT5 and to audit detailed profit/loss statistics.",
    keywords: ["trade", "trades", "history", "execution", "executed", "order", "orders", "pnl", "deal", "deals"]
  },
  accounts: {
    title: "🔌 Trading Accounts Page",
    path: "/accounts",
    purpose: "Allows you to connect, manage, and monitor your Metatrader 5 account connections.",
    what_to_see: [
      "A list of registered Metatrader 5 accounts",
      "Bridge connection status (green 'online' or grey 'offline' status ping dot)",
      "Broker credentials details (Login ID, Server name, Account Name)",
      "**+ Add Account** button to link a new MT5 terminal instance"
    ],
    why_use_it: "To link your broker credentials and MT5 terminal charts so they can receive copy execution commands.",
    keywords: ["account", "accounts", "mt5 account", "broker", "login", "credentials", "add account", "connect account", "trading accounts"]
  },
  logs: {
    title: "📋 Activity Logs Page",
    path: "/logs",
    purpose: "A deep system diagnostic terminal that logs backend copier actions.",
    what_to_see: [
      "Real-time streams of backend copier and database actions",
      "Validation processes and webhook parsing logs",
      "MT5 EA connection feedbacks and detailed rejection errors (e.g. invalid license, market closed)"
    ],
    why_use_it: "Essential for debugging failed signals, license authentication errors, DLL connectivity issues, or size parameters.",
    keywords: ["log", "logs", "activity log", "system log", "error", "failed", "troubleshoot", "debug", "reject", "rejection", "logs tab"]
  },
  downloads: {
    title: "🔑 Downloads & Licenses Page",
    path: "/downloads",
    purpose: "Your security center and script download page.",
    what_to_see: [
      "The official MT5 Copier EA script download link (\`.ex5\` file)",
      "Your generated active **License IDs** and their allocated slots",
      "**Generate License ID** button to create new authorizations (limits based on plans)"
    ],
    why_use_it: "To download the MT5 EA script and generate the license keys needed to validate your connection bridge.",
    keywords: ["download", "ea", "expert advisor", "ex5", "license", "licenses", "license key", "generate", "key", "keys"]
  },
  docs: {
    title: "📚 Documentation Page",
    path: "/docs",
    purpose: "A structured reference guide listing configurations and alert formatting.",
    what_to_see: [
      "Detailed step-by-step setup guides for MT5 and TradingView",
      "Copy-paste webhook JSON syntax templates",
      "WebRequest allowed URLs guidelines"
    ],
    why_use_it: "To learn the setup mechanics, understand webhook structures, and copy-paste exact payload formats.",
    keywords: ["docs", "documentation", "guide", "guidebook", "tutorial", "instructions", "help", "learn", "syntax"]
  },
  pricing: {
    title: "💳 Pricing & Plans Page",
    path: "/renew-plan",
    purpose: "Subscription hub where you can view pricing tiers, compare limits, and buy plans.",
    what_to_see: [
      "A comparison grid of active billing plans (permitting different license and connection volumes)",
      "Multi-step secure Checkout Wizard (KYC forms, Razorpay gate simulators, UPI payment methods)",
      "Simulated payment success celebrations"
    ],
    why_use_it: "To upgrade your connection limits, unlock additional license slots, or extend your validity timeline.",
    keywords: ["plan", "plans", "pricing", "renew", "upgrade", "buy", "pay", "payment", "razorpay", "upi", "card"]
  },
  profile: {
    title: "⚙️ Settings (Profile) Page",
    path: "/profile",
    purpose: "Lets you edit credentials, change security passwords, and toggle Obsidian/Alabaster visual themes.",
    what_to_see: [
      "Profile editing parameters (Full Name, email)",
      "Security credentials update (secure password change inputs)",
      "Interactive Theme selectors (Obsidian Dark and Ice Alabaster modes)"
    ],
    why_use_it: "To modify account credentials, change passwords, or swap visual interface layouts.",
    keywords: ["profile", "setting", "settings", "password", "theme", "dark mode", "light mode", "settings page"]
  },
  support: {
    title: "💬 Support Hub Page",
    path: "/support",
    purpose: "Lets you check your active support tier permissions and submit custom help tickets directly to our dev team.",
    what_to_see: [
      "Your plan's eligible Support Channels checker (Free chatbot, Email, Telegram, premium telephone lines)",
      "Create Technical Help Ticket form (Subject, Category selection, detailed problem description)",
      "Direct action buttons to contact support channels or upgrade tiers"
    ],
    why_use_it: "To submit a tech support ticket, seek help, or check direct active developer support channels.",
    keywords: ["support", "contact", "help", "ticket", "tickets", "create ticket", "submit ticket", "issue", "support hub"]
  }
};

const BOT_RESPONSES = {
  connect_mt5: `**To connect your MT5 EA successfully:**

1. Go to the **Downloads** page and download the copier EA (\`.ex5\` file).
2. Open your MT5 terminal, click **File ➔ Open Data Folder**.
3. Navigate to **MQL5 ➔ Experts** and paste the EA file.
4. Refresh the Expert Advisors list in MT5, then drag the EA onto any chart.
5. ⚠️ **CRITICAL STEP**: Go to **Tools ➔ Options ➔ Expert Advisors** and check:
   * **Allow DLL imports** (Required for secure bridge communication)
   * **Allow WebRequest for listed URL**
6. Add this backend URL to the WebRequest list: \`http://your-server-ip:5000\`
7. Generate a **License ID** from the **Downloads** page and paste it into the EA settings.`,

  check_executions: `**Checking Trade Execution & Signal Responses:**

1. **Trades (Trade History) Tab**:
   * Navigate to the **Trades** tab in the sidebar.
   * This displays all copied trades that were successfully executed on your MT5 terminal, including opening/closing prices, volume, magic numbers, and real-time profits/losses.
2. **Activity Logs Tab**:
   * Navigate to the **Activity Logs** (or **System Logs**) tab in the sidebar.
   * This shows a live, sequential log of all incoming TradingView alert webhooks and the corresponding execution results returned from your MT5 terminal.
   * If a trade failed to execute, you will find the specific error description here (e.g. *license_limit_reached*, *invalid_symbol*, *market_closed*, or *dll_imports_disabled*).
3. **Signals Tab**:
   * Navigate to the **Signals** tab in the sidebar.
   * This lists all incoming webhook payloads received from TradingView in real-time, regardless of execution outcome, letting you verify that your alerts are reaching the server successfully.`,

  license_id: `**License IDs & Limit Guidelines:**

* A **License ID** is required to authorize the MT5 EA connections.
* **Demo Plan**: Limited to **1 License ID** (connects 1 MT5 account).
* **Paid Plans**: Offer expanded limits for multiple MT5 accounts.
* **Over Limits**: If you try to generate more licenses than your plan allows, you will receive an "Upgrade Plan" alert.
* Navigate to the **Downloads** tab to view your active License IDs.`,

  webhook_format: `**TradingView Webhook Configuration:**

* Webhook URL: \`http://your-server-ip:5000/webhook\`
* Content-Type: Must be **application/json**
* **Standard JSON payload structure**:
\`\`\`json
{
  "license": "YOUR_LICENSE_ID",
  "action": "buy",
  "symbol": "EURUSD",
  "volume": 0.1,
  "magic": 123456,
  "tp": 50,
  "sl": 30
}
\`\`\`
* Ensure you paste your valid License ID in the payload. Check **Activity Logs** to track processed alerts!`,

  plans_pricing: `**Pricing Plans & Automatic Upgrades:**

* Explore our **Monthly & Yearly** plans in the **Pricing & Plans** tab!
* **Fast Checkout**: We accept UPI, Cards, Net Banking, and Bank Transfers via secure **Razorpay** integration.
* **Instant Expiry Updates**: Once payment is completed, your license limits, support tier, and expiry date will be updated automatically.`,

  support_channels: `**Support Tier Eligibility & Gated Channels:**

* **Demo Users**: Free self-service support via this automated **PineBot** assistant.
* **Starter / Professional plans**: Access premium channels including:
  * **Direct Email support**
  * **Telegram Support Bot**
  * **Premium Support Contact Number**
* Check your plan access and submit custom tech support tickets on the **Support Hub** page (\`/support\`). Need to unlock premium channels? Go to **Pricing & Plans** to upgrade!`
};

const DEFAULT_FALLBACK = `I couldn't find a direct match for that query. 

Let's get you sorted! Select one of the preset quick topics below, or ask me where a page is located (e.g., **"where can I see my trades?"**, **"where do I download the EA?"**, **"explain the dashboard page"**).`;

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("pine_connector_chat");
    return saved ? JSON.parse(saved) : [
      {
        id: "welcome",
        sender: "bot",
        text: "Hello! I am **PineBot**, your virtual assistant. How can I help you automate your TradingView alerts today?",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ];
  });
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);

  // Save chat to localStorage
  useEffect(() => {
    localStorage.setItem("pine_connector_chat", JSON.stringify(messages));
  }, [messages]);

  // Handle auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Global event listener to open chatbot from external components
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener("open-pinebot", handleOpen);
    return () => window.removeEventListener("open-pinebot", handleOpen);
  }, []);

  // ── Async DB-backed search (hits MongoDB project_docs) ────────────────
  const searchDocsDB = async (query) => {
    try {
      const result = await searchProjectDocs(query, "", 3);
      if (result.docs && result.docs.length > 0) {
        const doc = result.docs[0];
        return `📚 **${doc.title}**\n\n${doc.content}`;
      }
    } catch (e) {
      // Silently fall through to DEFAULT_FALLBACK if API fails
    }
    return null;
  };

  const getSystemResponse = (text) => {
    const query = text.toLowerCase().trim();

    // 1. Identify intent (multilingual English & Hindi matching tags)
    const isNavigational = query.includes("kahan") || query.includes("kaha") || query.includes("where") || 
                           query.includes("dekh") || query.includes("show") || query.includes("view") || 
                           query.includes("find") || query.includes("locate") || query.includes("tab") ||
                           query.includes("page") || query.includes("route") || query.includes("screen") ||
                           query.includes("link");

    const isDescriptive = query.includes("kya") || query.includes("what") || query.includes("kyun") || 
                          query.includes("why") || query.includes("use") || query.includes("explain") || 
                          query.includes("purpose") || query.includes("kaise") || query.includes("how") ||
                          query.includes("details");

    // 2. Score each page guide
    let bestPage = null;
    let bestScore = 0;

    Object.keys(PAGE_GUIDES).forEach(key => {
      const page = PAGE_GUIDES[key];
      let score = 0;
      page.keywords.forEach(keyword => {
        if (query.includes(keyword)) {
          score += 1.5; // Strong weight for exact page match
        }
      });
      if (score > bestScore) {
        bestScore = score;
        bestPage = page;
      }
    });

    // 3. Generate response if a page matched strongly
    if (bestPage && bestScore >= 1.5) {
      const bulletList = bestPage.what_to_see.map(item => `* ${item}`).join("\n");
      
      if (isNavigational) {
        return `📍 **Where is it located?**
        
You can find this by clicking on the **${bestPage.title}** option in your sidebar menu (or directly visiting \`${bestPage.path}\`).

**On this page, you can see:**
${bulletList}

* **Why use it:** ${bestPage.why_use_it}`;
      }
      
      if (isDescriptive) {
        return `📖 **About the ${bestPage.title} (\`${bestPage.path}\`):**

* **Core Purpose:** ${bestPage.purpose}
* **What you can see & do here:**
${bulletList}

* **Why use it:** ${bestPage.why_use_it}`;
      }

      // Default page info response
      return `ℹ️ **Here is information on the ${bestPage.title}:**

* **Sidebar Route:** \`${bestPage.path}\`
* **Purpose:** ${bestPage.purpose}
* **What's available here:**
${bulletList}

👉 *Click the **${bestPage.title}** tab in the left sidebar menu to navigate to it!*`;
    }

    // Check specific signal response/execution logs queries first
    if (
      query.includes("execute") || 
      query.includes("response") || 
      query.includes("log") || 
      query.includes("history") || 
      query.includes("success") || 
      query.includes("fail") || 
      query.includes("check")
    ) {
      return BOT_RESPONSES.check_executions;
    }

    // Check predefined keys
    if (query.includes("mt5") || query.includes("ea") || query.includes("expert") || query.includes("connect")) {
      return BOT_RESPONSES.connect_mt5;
    }
    if (query.includes("license") || query.includes("licence") || query.includes("key") || query.includes("limit")) {
      return BOT_RESPONSES.license_id;
    }
    if (query.includes("signal") || query.includes("webhook") || query.includes("json") || query.includes("payload")) {
      return BOT_RESPONSES.webhook_format;
    }
    if (query.includes("plan") || query.includes("price") || query.includes("pricing") || query.includes("pay") || query.includes("razorpay") || query.includes("buy")) {
      return BOT_RESPONSES.plans_pricing;
    }
    if (query.includes("support") || query.includes("contact") || query.includes("help") || query.includes("email") || query.includes("telegram")) {
      return BOT_RESPONSES.support_channels;
    }

    return DEFAULT_FALLBACK;
  };

  const handleSend = (textToSend) => {
    const text = textToSend || inputValue;
    if (!text.trim()) return;

    const userMsg = {
      id: Date.now().toString(),
      sender: "user",
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInputValue("");

    // Simulate Bot Typing + DB lookup
    setIsTyping(true);
    
    // First try local keyword match
    const localResponse = getSystemResponse(text);
    const isLocalFallback = localResponse === DEFAULT_FALLBACK;

    if (!isLocalFallback) {
      // Local match found — respond quickly
      setTimeout(() => {
        const botMsg = {
          id: (Date.now() + 1).toString(),
          sender: "bot",
          text: localResponse,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages((prev) => [...prev, botMsg]);
        setIsTyping(false);
      }, 850);
    } else {
      // Local fallback — try DB search
      searchDocsDB(text).then(dbResponse => {
        const responseText = dbResponse || DEFAULT_FALLBACK;
        const botMsg = {
          id: (Date.now() + 1).toString(),
          sender: "bot",
          text: dbResponse 
            ? `🔍 *Found in knowledge base:*\n\n${dbResponse}`
            : DEFAULT_FALLBACK,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages((prev) => [...prev, botMsg]);
        setIsTyping(false);
      });
    }
  };

  const handlePresetClick = (topic) => {
    const userMsgText = `Tell me more about: ${topic.label.substring(3)}`;
    const userMsg = {
      id: Date.now().toString(),
      sender: "user",
      text: userMsgText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);

    setIsTyping(true);
    setTimeout(() => {
      const botMsg = {
        id: (Date.now() + 1).toString(),
        sender: "bot",
        text: BOT_RESPONSES[topic.id] || DEFAULT_FALLBACK,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 700);
  };

  const handleClearChat = () => {
    if (window.confirm("Clear active chat history?")) {
      const initial = [
        {
          id: "welcome",
          sender: "bot",
          text: "Hello! I am **PineBot**, your virtual assistant. How can I help you automate your TradingView alerts today?",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ];
      setMessages(initial);
    }
  };

  // Helper to format basic markdown highlights in chatbot
  const formatMarkdown = (text) => {
    if (!text) return "";
    let formatted = text;
    // Replace **bold** with <strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    // Replace inline code `code`
    formatted = formatted.replace(/`(.*?)`/g, "<code class='bot-inline-code'>$1</code>");
    // Replace list bullets
    formatted = formatted.replace(/^\*\s(.*)$/gm, "<li>$1</li>");
    // Code blocks
    formatted = formatted.replace(/```json([\s\S]*?)```/g, "<pre class='bot-code-block'><code>$1</code></pre>");
    // Handle list blocks
    if (formatted.includes("<li>")) {
      // Very basic wrapping
      const parts = formatted.split("\n");
      let inList = false;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].includes("<li>")) {
          if (!inList) {
            parts[i] = "<ul>" + parts[i];
            inList = true;
          }
        } else {
          if (inList) {
            parts[i - 1] = parts[i - 1] + "</ul>";
            inList = false;
          }
        }
      }
      formatted = parts.join("\n");
    }
    // Replace newlines with <br /> outside of pre tags
    return formatted.split("\n").map((line, idx) => {
      if (line.includes("<pre") || line.includes("<code") || line.includes("<li>") || line.includes("<ul>") || line.includes("</ul")) {
        return line;
      }
      return line + "<br />";
    }).join("");
  };

  return (
    <>
      {/* Floating Action Button */}
      <button 
        className={`chatbot-trigger-btn ${isOpen ? "active" : ""}`} 
        onClick={() => setIsOpen(!isOpen)}
        title="Open Support Chatbot"
      >
        {isOpen ? <X size={24} /> : <Bot size={24} className="pulse-icon" />}
        <span className="glow-ring"></span>
      </button>

      {/* Chat Panel Box */}
      <div className={`chatbot-panel ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-title-row">
            <div className="bot-avatar-header">
              <Bot size={18} />
              <span className="online-dot"></span>
            </div>
            <div>
              <h3>PineBot</h3>
              <p>Platform Support Assistant</p>
            </div>
          </div>
          <div className="chatbot-header-actions">
            <button className="icon-action-btn" onClick={handleClearChat} title="Reset Chat">
              <Trash2 size={15} />
            </button>
            <button className="icon-action-btn" onClick={() => setIsOpen(false)} title="Minimize">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Message Panel Area */}
        <div className="chatbot-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`chat-bubble-row ${msg.sender}`}>
              {msg.sender === "bot" && (
                <div className="bot-bubble-avatar">
                  <Bot size={14} />
                </div>
              )}
              <div className="chat-bubble-container">
                <div 
                  className="chat-bubble"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.text) }}
                />
                <span className="chat-time">{msg.timestamp}</span>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="chat-bubble-row bot">
              <div className="bot-bubble-avatar">
                <Bot size={14} />
              </div>
              <div className="chat-bubble-container">
                <div className="chat-bubble typing">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Preset Chips Carousel */}
        <div className="chatbot-preset-carousel">
          {PRESET_TOPICS.map((topic) => (
            <button 
              key={topic.id} 
              className="preset-chip-btn"
              onClick={() => handlePresetClick(topic)}
            >
              {topic.label}
              <ArrowUpRight size={12} className="chip-arrow" />
            </button>
          ))}
        </div>

        {/* Send Input Bar */}
        <form 
          className="chatbot-input-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
        >
          <input
            type="text"
            placeholder="Type a platform question..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button type="submit" className="chatbot-send-btn" disabled={!inputValue.trim()}>
            <Send size={16} />
          </button>
        </form>
      </div>
    </>
  );
}
